import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useConnectionStore } from '../store/connectionStore';
import { useChatStore } from '../store/chatStore';
import { UserRole, SURVEY_CATEGORIES } from '../types';
import { useTheme } from '../theme';
import { AnimatedIcon, IconName } from '../components/icons';
// @ts-ignore
import SurveyConnectLogo from '../assets/survey_connect_logo.png';

type NavItem = { path: string; label: string; icon: IconName; exact?: boolean };

const CLIENT_NAV: NavItem[] = [
  { path: '/client', label: 'Dashboard', icon: 'clipboard', exact: true },
  { path: '/client/request/create', label: 'New Request', icon: 'pen' },
  { path: '/client/categories', label: 'Categories', icon: 'folder' },
  { path: '/client/map', label: 'Map', icon: 'location' },
  { path: '/client/messages', label: 'Messages', icon: 'chat' },
  { path: '/client/history', label: 'Survey History', icon: 'folder' },
];

const SURVEYOR_NAV: NavItem[] = [
  { path: '/surveyor', label: 'Dashboard', icon: 'clipboard', exact: true },
  { path: '/surveyor/browse-jobs', label: 'Browse Jobs', icon: 'search' },
  { path: '/surveyor/map', label: 'Map', icon: 'location' },
  { path: '/surveyor/advertise-jobs', label: 'Advertise Job', icon: 'pen' },
  { path: '/surveyor/messages', label: 'Messages', icon: 'chat' },
  { path: '/surveyor/history', label: 'Job History', icon: 'folder' },
  { path: '/surveyor/profile', label: 'Profile', icon: 'user' },
];

const ADMIN_NAV: NavItem[] = [
  { path: '/admin', label: 'Overview', icon: 'chart', exact: true },
  { path: '/admin/requests', label: 'Requests', icon: 'pen' },
  { path: '/admin/map', label: 'Map', icon: 'location' },
  { path: '/admin/job-leads', label: 'Job Leads', icon: 'folder' },
  { path: '/admin/categories', label: 'Categories', icon: 'folder' },
  { path: '/admin/chat-oversight', label: 'Chat Oversight', icon: 'chat' },
  { path: '/admin/anti-cheat', label: 'Anti-Cheat', icon: 'shield' },
  { path: '/admin/negotiations', label: 'Negotiations', icon: 'chat' },
  { path: '/admin/deals', label: 'Deals', icon: 'clipboard' },
  { path: '/admin/clients', label: 'Clients', icon: 'user' },
  { path: '/admin/surveyors', label: 'Surveyors', icon: 'worker' },
  { path: '/admin/audit-logs', label: 'Audit Logs', icon: 'folder' },
  { path: '/admin/history', label: 'Survey History', icon: 'folder' },
  { path: '/admin/settings', label: 'Settings', icon: 'settings' },
];

function getNav(role?: UserRole) {
  switch (role) {
    case UserRole.ADMIN: return ADMIN_NAV;
    case UserRole.SURVEYOR: return SURVEYOR_NAV;
    default: return CLIENT_NAV;
  }
}

function getRoleLabel(role?: UserRole) {
  switch (role) {
    case UserRole.ADMIN: return 'Administrator';
    case UserRole.SURVEYOR: return 'Surveyor';
    default: return 'Client';
  }
}

export const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuthStore();
  const { isOnline, notificationCount, clearNotifications } = useConnectionStore();
  const { conversations } = useChatStore();
  const { isDark, toggle: toggleTheme } = useTheme();
  
  const unreadMessages = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);
  const navigate = useNavigate();
  const location = useLocation();
  
  const [searchText, setSearchText] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isUserSheetOpen, setIsUserSheetOpen] = useState(false);
  const [isMoreSheetOpen, setIsMoreSheetOpen] = useState(false);
  
  const nav = getNav(user?.role);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/categories')) {
      setOpenDropdown('categories');
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    setIsUserSheetOpen(false);
    await logout();
    navigate('/login');
  };

  const isActive = (item: NavItem) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const getRoleHome = () => {
    switch (user?.role) {
      case UserRole.ADMIN: return '/admin';
      case UserRole.SURVEYOR: return '/surveyor';
      default: return '/client';
    }
  };

  const isDashboardHome = 
    location.pathname === '/client' || 
    location.pathname === '/client/' || 
    location.pathname === '/surveyor' || 
    location.pathname === '/surveyor/';

  // Tab bar logic: visible tabs vs "More" sheet tabs
  const hasMore = nav.length > 5;
  const visibleTabs = hasMore ? nav.slice(0, 4) : nav;
  const moreTabs = hasMore ? nav.slice(4) : [];

  if (isMobile) {
    return (
      <div style={styles.mobileRoot} className="sc-no-tap-highlight">
        {/* Status banners */}
        {!isOnline && (
          <div style={styles.offlineBanner}>
            <AnimatedIcon name="warning" size={14} color="#FFF" />
            <span style={{ marginLeft: 6 }}>Offline — Check connection</span>
          </div>
        )}

        {/* Dynamic PayPal-Style Mobile Header */}
        <header style={{
          ...styles.mobileHeader,
          background: isDashboardHome ? 'var(--sc-bg-hero-gradient)' : 'var(--sc-bg-secondary)',
          borderBottom: isDashboardHome ? 'none' : '1px solid var(--sc-border-light)',
        }}>
          <div style={styles.mobileHeaderLeft} onClick={() => setIsUserSheetOpen(true)}>
            <div style={{
              ...styles.mobileAvatar,
              border: isDashboardHome ? '1.5px solid rgba(255, 255, 255, 0.6)' : 'none',
              background: isDashboardHome ? 'rgba(255, 255, 255, 0.15)' : 'var(--sc-brand-gradient)',
              color: isDashboardHome ? '#FFFFFF' : 'var(--sc-text-on-brand)',
            }}>
              {(user?.first_name?.[0] || 'U').toUpperCase()}
            </div>
          </div>
          <div style={styles.mobileHeaderCenter} onClick={() => navigate(getRoleHome())}>
            <img
              src={SurveyConnectLogo}
              alt="Survey Connect Logo"
              style={{
                height: 24,
                objectFit: 'contain',
                maxWidth: '100%',
                filter: isDashboardHome ? 'brightness(0) invert(1)' : 'var(--sc-logo-filter)',
              }}
            />
          </div>
          <div style={styles.mobileHeaderRight}>
            <div style={{
              ...styles.mobileIconBtn,
              background: isDashboardHome ? 'rgba(255, 255, 255, 0.1)' : 'var(--sc-bg-hover)',
            }} onClick={() => navigate(
              user?.role === UserRole.SURVEYOR ? '/surveyor/messages' : user?.role === UserRole.ADMIN ? '/admin/chat-oversight' : '/client/messages'
            )}>
              <AnimatedIcon name="chat" size={18} color={isDashboardHome ? '#FFFFFF' : 'var(--sc-text-primary)'} />
              {unreadMessages > 0 && (
                <span style={styles.mobileBadge}>{unreadMessages > 99 ? '99+' : unreadMessages}</span>
              )}
            </div>
            <div style={{
              ...styles.mobileIconBtn,
              background: isDashboardHome ? 'rgba(255, 255, 255, 0.1)' : 'var(--sc-bg-hover)',
            }} onClick={clearNotifications}>
              <AnimatedIcon name="bell" size={18} color={isDashboardHome ? '#FFFFFF' : 'var(--sc-text-primary)'} />
              {notificationCount > 0 && (
                <span style={styles.mobileBadgeRed}>{notificationCount > 99 ? '99+' : notificationCount}</span>
              )}
            </div>
          </div>
        </header>

        {/* Mobile Main Content */}
        <main style={{
          ...styles.mobileContent,
          padding: isDashboardHome ? '0 0 76px 0' : '16px 16px 76px 16px',
          background: 'var(--sc-bg-primary)',
        }} key={location.pathname} className="sc-animate-fade-in-up">
          <Outlet />
        </main>

        {/* Bottom Tab Bar */}
        <nav style={styles.mobileTabBar}>
          {visibleTabs.map((item) => {
            const active = isActive(item) && !isMoreSheetOpen;
            return (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  ...styles.mobileTabItem,
                  color: active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                }}
              >
                <div style={{ transform: active ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AnimatedIcon
                    name={item.icon}
                    size={20}
                    color={active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)'}
                  />
                </div>
                <span style={{
                  ...styles.mobileTabLabel,
                  color: active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                  fontWeight: active ? '700' : '500',
                }}>
                  {item.label === 'Overview' || item.label === 'Dashboard' ? 'Home' : item.label}
                </span>
              </Link>
            );
          })}
          {hasMore && (
            <button
              onClick={() => setIsMoreSheetOpen(true)}
              style={{
                ...styles.mobileTabItem,
                color: isMoreSheetOpen ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              <div style={{ transform: isMoreSheetOpen ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AnimatedIcon
                  name="folder"
                  size={20}
                  color={isMoreSheetOpen ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)'}
                />
              </div>
              <span style={{
                ...styles.mobileTabLabel,
                color: isMoreSheetOpen ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                fontWeight: isMoreSheetOpen ? '700' : '500',
              }}>
                More
              </span>
            </button>
          )}
        </nav>

        {/* User Account / Settings Drawer Sheet */}
        {isUserSheetOpen && (
          <div style={styles.sheetOverlay} onClick={() => setIsUserSheetOpen(false)}>
            <div style={styles.sheetContainer} className="sc-animate-sheet" onClick={(e) => e.stopPropagation()}>
              <div style={styles.sheetHandle} />
              
              <div style={{ textAlign: 'center', padding: '12px 0 20px' }}>
                <div style={{
                  ...styles.mobileAvatar,
                  width: 52,
                  height: 52,
                  fontSize: 20,
                  margin: '0 auto 10px',
                  background: 'var(--sc-brand-gradient)',
                  boxShadow: 'var(--sc-shadow-md)',
                }}>
                  {(user?.first_name?.[0] || 'U').toUpperCase()}
                </div>
                <h3 style={styles.sheetTitle}>{user?.first_name} {user?.last_name}</h3>
                <p style={styles.sheetSubtitle}>{getRoleLabel(user?.role)}</p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {user?.role === UserRole.SURVEYOR && (
                  <button style={styles.sheetMenuItem} onClick={() => { setIsUserSheetOpen(false); navigate('/surveyor/profile'); }}>
                    <AnimatedIcon name="user" size={18} color="var(--sc-text-primary)" />
                    <span style={styles.sheetMenuItemText}>Profile Details</span>
                  </button>
                )}
                {user?.role === UserRole.CLIENT && (
                  <button style={styles.sheetMenuItem} onClick={() => { setIsUserSheetOpen(false); navigate('/client/history'); }}>
                    <AnimatedIcon name="folder" size={18} color="var(--sc-text-primary)" />
                    <span style={styles.sheetMenuItemText}>Survey History</span>
                  </button>
                )}
                <button style={styles.sheetMenuItem} onClick={() => { toggleTheme(); }}>
                  <AnimatedIcon name={isDark ? 'sun' : 'moon'} size={18} color="var(--sc-text-primary)" />
                  <span style={styles.sheetMenuItemText}>{isDark ? 'Light Theme' : 'Dark Theme'}</span>
                </button>
                
                <div style={{ height: 1, background: 'var(--sc-border-light)', margin: '8px 0' }} />
                
                <button style={{ ...styles.sheetMenuItem, color: 'var(--sc-error)' }} onClick={handleLogout}>
                  <AnimatedIcon name="warning" size={18} color="var(--sc-error)" />
                  <span style={{ ...styles.sheetMenuItemText, color: 'var(--sc-error)' }}>Log Out</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* More Actions Drawer Sheet */}
        {isMoreSheetOpen && (
          <div style={styles.sheetOverlay} onClick={() => setIsMoreSheetOpen(false)}>
            <div style={styles.sheetContainer} className="sc-animate-sheet" onClick={(e) => e.stopPropagation()}>
              <div style={styles.sheetHandle} />
              <h3 style={styles.sheetTitle}>All Portals</h3>
              
              <div style={styles.sheetGrid}>
                {moreTabs.map((item) => {
                  const active = isActive(item);
                  return (
                    <div
                      key={item.path}
                      onClick={() => {
                        setIsMoreSheetOpen(false);
                        navigate(item.path);
                      }}
                      style={{
                        ...styles.sheetGridItem,
                        background: active ? 'var(--sc-bg-active)' : 'var(--sc-bg-input)',
                        border: active ? '1px solid var(--sc-brand-primary-light)' : '1px solid var(--sc-border)',
                      }}
                    >
                      <AnimatedIcon
                        name={item.icon}
                        size={20}
                        color={active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)'}
                      />
                      <span style={{
                        fontSize: 12,
                        fontWeight: active ? '600' : '500',
                        color: active ? 'var(--sc-brand-primary)' : 'var(--sc-text-primary)',
                        textAlign: 'center',
                        marginTop: 6,
                      }}>
                        {item.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop View
  return (
    <div style={styles.root}>
      {/* Status banner */}
      {!isOnline && (
        <div style={styles.offlineBanner}>
          <AnimatedIcon name="warning" size={16} color="#FFF" />
          <span style={{ marginLeft: 8 }}>You are offline — some features may be unavailable</span>
        </div>
      )}

      <div style={styles.layout}>
        {/* Sidebar */}
        <aside style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', width: '100%' }}>
              <img
                src={SurveyConnectLogo}
                alt="Survey Connect Logo"
                style={{
                  height: 28,
                  objectFit: 'contain',
                  maxWidth: '90%',
                  filter: 'var(--sc-logo-filter)',
                }}
              />
            </div>
          </div>
          
          <div style={styles.sidebarDivider} />
          <div style={styles.menuLabel}>PORTAL SECTIONS</div>

          {/* Nav */}
          <nav style={styles.nav}>
            {nav.map((item) => {
              const active = isActive(item);
              const isCatItem = item.path.endsWith('/categories');

              if (isCatItem) {
                const isOpen = openDropdown === 'categories';
                const activeCatId = location.pathname === item.path ? (new URLSearchParams(location.search)).get('cat') : null;
                return (
                  <div key={item.path}>
                    <div
                      onClick={() => setOpenDropdown(isOpen ? null : 'categories')}
                      style={{
                        ...styles.navItem,
                        background: active ? 'var(--sc-bg-hover)' : 'transparent',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      {active && <div style={styles.activeBar} />}
                      <span style={{ ...styles.navIcon, color: active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)', marginRight: 12 }}>
                        <AnimatedIcon name={item.icon} size={18} color={active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)'} />
                      </span>
                      <span style={{ ...styles.navLabel, color: active ? 'var(--sc-text-primary)' : 'var(--sc-text-secondary)', fontWeight: active ? 600 : 500, flex: 1 }}>
                        {item.label}
                      </span>
                      <span style={{ color: 'var(--sc-text-tertiary)', fontSize: 10, marginRight: 4 }}>
                        {isOpen ? '▲' : '▼'}
                      </span>
                    </div>
                    {isOpen && (
                      <div style={{ paddingLeft: 12, paddingBottom: 4 }}>
                        {SURVEY_CATEGORIES.map((cat) => {
                          const catActive = activeCatId === cat.id;
                          return (
                            <Link
                              key={cat.id}
                              to={`${item.path}?cat=${cat.id}`}
                              style={{
                                ...styles.navItem,
                                padding: '7px 10px 7px 12px',
                                background: catActive ? 'var(--sc-bg-active)' : 'transparent',
                                borderLeft: `2px solid ${catActive ? cat.color : 'var(--sc-border)'}`,
                                borderRadius: 0,
                                marginBottom: 1,
                              }}
                            >
                              <span style={{ fontSize: 14, marginRight: 8, lineHeight: 1 }}>{cat.icon}</span>
                              <span style={{
                                fontSize: 12,
                                color: catActive ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                                fontWeight: catActive ? 600 : 400,
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>{cat.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    ...styles.navItem,
                    background: active ? 'var(--sc-bg-hover)' : 'transparent',
                  }}
                >
                  {active && <div style={styles.activeBar} />}
                  <span style={{ 
                    ...styles.navIcon, 
                    color: active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)',
                    marginRight: 12,
                  }}>
                    <AnimatedIcon name={item.icon} size={18} color={active ? 'var(--sc-brand-primary)' : 'var(--sc-text-secondary)'} />
                  </span>
                  <span style={{ 
                    ...styles.navLabel,
                    color: active ? 'var(--sc-text-primary)' : 'var(--sc-text-secondary)',
                    fontWeight: active ? 600 : 500,
                    flex: 1,
                  }}>{item.label}</span>
                  {item.label === 'Messages' && unreadMessages > 0 && (
                    <span style={styles.navBadge}>{unreadMessages > 99 ? '99+' : unreadMessages}</span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Footer */}
          <div style={styles.sidebarFooter}>
            <div style={styles.userCard}>
              <div style={styles.userAvatar}>
                {(user?.first_name?.[0] || 'U').toUpperCase()}
              </div>
              <div style={styles.userInfo}>
                <div style={styles.userName}>{user?.first_name} {user?.last_name}</div>
                <div style={styles.userRole}>{getRoleLabel(user?.role)}</div>
              </div>
            </div>
            
            <button style={{ ...styles.themeToggleBtn, marginBottom: 8 }} onClick={toggleTheme}>
              <AnimatedIcon name={isDark ? 'sun' : 'moon'} size={14} color="var(--sc-text-secondary)" />
              <span style={{ marginLeft: 8 }}>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
            </button>

            <button onClick={handleLogout} style={styles.logoutBtn}>
              Log Out
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <div style={styles.main}>
          <header style={styles.topbar}>
            <div style={styles.searchWrap}>
              <span style={styles.searchIcon}><AnimatedIcon name="search" size={18} color="var(--sc-text-tertiary)" /></span>
              <input
                type="text"
                placeholder="Search resources, requests, surveyors..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            <div style={styles.topRight}>
              <div style={styles.bellWrap} onClick={() => navigate(user?.role === 'SURVEYOR' ? '/surveyor/messages' : user?.role === 'ADMIN' ? '/admin/chat-oversight' : '/client/messages')}>
                <AnimatedIcon name="chat" size={20} color="var(--sc-text-secondary)" />
                {unreadMessages > 0 && (
                  <span style={styles.msgBadge}>{unreadMessages > 99 ? '99+' : unreadMessages}</span>
                )}
              </div>
              <div style={styles.bellWrap} onClick={clearNotifications}>
                <AnimatedIcon name="bell" size={20} color="var(--sc-text-secondary)" />
                {notificationCount > 0 && (
                  <span style={styles.notifBadge}>{notificationCount > 99 ? '99+' : notificationCount}</span>
                )}
              </div>
            </div>
          </header>

          <main style={styles.content}>
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  root: { 
    display: 'flex', 
    flexDirection: 'column', 
    height: '100vh', 
    fontFamily: "var(--sc-font-family)", 
    background: 'var(--sc-bg-primary)', 
    overflow: 'hidden',
    color: 'var(--sc-text-primary)',
  },

  offlineBanner: { 
    background: 'var(--sc-error)', 
    color: '#FFF', 
    textAlign: 'center', 
    padding: '8px 16px', 
    fontSize: 13, 
    fontWeight: 600, 
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  layout: { display: 'flex', flex: 1, overflow: 'hidden' },

  sidebar: { 
    width: 240,
    margin: '16px 0 16px 16px',
    padding: '24px 16px',
    borderRadius: 'var(--sc-radius-lg)',
    background: 'var(--sc-bg-secondary)',
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-md)',
    display: 'flex', 
    flexDirection: 'column', 
    overflow: 'hidden',
    position: 'relative',
    zIndex: 100,
    flexShrink: 0,
  },

  sidebarHeader: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 },
  systemName: { fontSize: 20, fontWeight: 800, color: 'var(--sc-text-primary)', letterSpacing: '-0.5px', fontFamily: "var(--sc-font-display)" },
  sidebarDivider: { height: 1, background: 'var(--sc-border-light)', borderRadius: 1, marginBottom: 20 },

  menuLabel: { fontSize: 10, fontWeight: 700, color: 'var(--sc-text-tertiary)', letterSpacing: '1.5px', marginBottom: 12, paddingLeft: 12 },

  nav: { 
    flex: 1, 
    display: 'flex', 
    flexDirection: 'column', 
    gap: 4, 
    overflowY: 'auto', 
    overflowX: 'hidden', 
    scrollbarWidth: 'none', 
  },
  navItem: { 
    display: 'flex', 
    alignItems: 'center', 
    padding: '12px', 
    borderRadius: 'var(--sc-radius-sm)', 
    cursor: 'pointer', 
    textDecoration: 'none', 
    position: 'relative', 
    transition: 'all var(--sc-transition-fast)',
    height: 42,
  },
  activeBar: { position: 'absolute', left: 0, top: 10, bottom: 10, width: 4, background: 'var(--sc-brand-primary)', borderRadius: '0 4px 4px 0', boxShadow: '0 0 8px var(--sc-brand-primary-light)' },
  navIcon: { fontSize: 18, width: 24, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navLabel: { fontSize: 13.5, fontFamily: 'inherit' },

  sidebarFooter: { marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--sc-border-light)', flexShrink: 0 },
  userCard: { display: 'flex', alignItems: 'center', gap: 10, padding: '0 0 14px' },
  userAvatar: { width: 34, height: 34, borderRadius: '50%', background: 'var(--sc-brand-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13.5, fontWeight: 700, color: '#FFF', boxShadow: 'var(--sc-shadow-sm)', flexShrink: 0 },
  userInfo: { flex: 1, minWidth: 0 },
  userName: { fontSize: 13, fontWeight: 600, color: 'var(--sc-text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  userRole: { fontSize: 10.5, color: 'var(--sc-text-tertiary)' },
  logoutBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '9px 0', background: 'var(--sc-error)', border: 'none', borderRadius: 'var(--sc-radius-sm)', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: '#FFFFFF', fontFamily: 'inherit', transition: 'all var(--sc-transition-fast)' },
  themeToggleBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '8px 0', background: 'transparent', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-sm)', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: 'var(--sc-text-secondary)', fontFamily: 'inherit', transition: 'all var(--sc-transition-fast)' },
  demoBadge: { marginTop: 6, display: 'inline-block', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: 6, padding: '2px 10px', fontSize: 9, fontWeight: 800, color: 'var(--sc-warning)', letterSpacing: 1, textTransform: 'uppercase' },

  /* Main Area */
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '0' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 32px 12px', background: 'transparent' },
  searchWrap: { display: 'flex', alignItems: 'center', background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-md)', padding: '8px 16px', minWidth: 320, gap: 10, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-xs)', transition: 'all var(--sc-transition-fast)' },
  searchIcon: { fontSize: 14, color: 'var(--sc-text-tertiary)' },
  searchInput: { border: 'none', background: 'transparent', outline: 'none', fontSize: 13.5, color: 'var(--sc-text-primary)', flex: 1, fontFamily: 'inherit' },
  topRight: { display: 'flex', alignItems: 'center', gap: 12 },
  bellWrap: { position: 'relative', cursor: 'pointer', width: 38, height: 38, background: 'var(--sc-bg-secondary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-xs)', transition: 'all var(--sc-transition-fast)' },
  msgBadge: { position: 'absolute', top: -2, right: -2, background: 'var(--sc-brand-primary)', color: '#FFF', fontSize: 9, fontWeight: 800, borderRadius: 8, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', border: '2px solid var(--sc-bg-primary)' },
  notifBadge: { position: 'absolute', top: -2, right: -2, background: 'var(--sc-error)', color: '#FFF', fontSize: 9, fontWeight: 800, borderRadius: 8, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', border: '2px solid var(--sc-bg-primary)' },
  navBadge: { background: 'var(--sc-brand-primary)', color: '#FFF', fontSize: 9, fontWeight: 800, borderRadius: 8, minWidth: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', marginLeft: 4, flexShrink: 0 },
  demoTag: { display: 'flex', alignItems: 'center', gap: 5, background: 'var(--sc-warning-bg)', border: '1px solid var(--sc-warning)', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 700, color: 'var(--sc-warning-text)' },

  content: { flex: 1, overflow: 'auto', padding: '0 32px 32px' },

  /* Mobile Layout Styles */
  mobileRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: 'var(--sc-bg-primary)',
    fontFamily: "var(--sc-font-family)",
    overflow: 'hidden',
  },
  mobileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    height: 'var(--sc-header-height)',
    flexShrink: 0,
    zIndex: 100,
    paddingTop: 'calc(12px + env(safe-area-inset-top, 0px))',
  },
  mobileHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  mobileAvatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: 13,
    boxShadow: 'var(--sc-shadow-sm)',
    transition: 'all var(--sc-transition-fast)',
  },
  mobileHeaderCenter: {
    flex: 1,
    textAlign: 'center',
    cursor: 'pointer',
  },
  mobileHeaderTitle: {
    fontFamily: "var(--sc-font-display)",
    fontWeight: '800',
    fontSize: 18,
    letterSpacing: '-0.5px',
  },
  mobileHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  mobileIconBtn: {
    position: 'relative',
    cursor: 'pointer',
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mobileBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    background: 'var(--sc-brand-primary)',
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: '800',
    borderRadius: 8,
    minWidth: 14,
    height: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 3px',
    border: '1.5px solid var(--sc-bg-secondary)',
  },
  mobileBadgeRed: {
    position: 'absolute',
    top: -2,
    right: -2,
    background: 'var(--sc-error)',
    color: '#FFFFFF',
    fontSize: 8,
    fontWeight: '800',
    borderRadius: 8,
    minWidth: 14,
    height: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 3px',
    border: '1.5px solid var(--sc-bg-secondary)',
  },
  mobileContent: {
    flex: 1,
    overflowY: 'auto',
  },
  mobileTabBar: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: 'var(--sc-tab-bar-height)',
    background: 'var(--sc-bg-tab-bar)',
    borderTop: '1px solid var(--sc-border-light)',
    boxShadow: 'var(--sc-shadow-tab-bar)',
    zIndex: 1000,
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },
  mobileTabItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    flex: 1,
    height: '100%',
    padding: 0,
    border: 'none',
    background: 'transparent',
  },
  mobileTabLabel: {
    fontSize: 10,
    marginTop: 4,
    fontFamily: "var(--sc-font-family)",
  },

  /* Sheet styles */
  sheetOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'var(--sc-bg-overlay)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    animation: 'sc-fade-in 0.25s cubic-bezier(0.4, 0, 0.2, 1) forwards',
  },
  sheetContainer: {
    width: '100%',
    maxWidth: 480,
    background: 'var(--sc-bg-sheet)',
    borderTopLeftRadius: 'var(--sc-radius-2xl)',
    borderTopRightRadius: 'var(--sc-radius-2xl)',
    padding: '16px 20px 24px',
    boxShadow: 'var(--sc-shadow-xl)',
    animation: 'sc-sheet-up 0.35s cubic-bezier(0.32, 0.72, 0, 1) forwards',
    maxHeight: '80vh',
    overflowY: 'auto',
    paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
  },
  sheetHandle: {
    width: 36,
    height: 5,
    borderRadius: 2.5,
    background: 'var(--sc-border)',
    margin: '0 auto 16px',
  },
  sheetTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: 'var(--sc-text-primary)',
    margin: '0 0 4px',
    textAlign: 'center',
  },
  sheetSubtitle: {
    fontSize: 12,
    color: 'var(--sc-text-secondary)',
    margin: 0,
    textAlign: 'center',
  },
  sheetMenu: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginTop: 16,
  },
  sheetMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 16px',
    borderRadius: 'var(--sc-radius-md)',
    background: 'transparent',
    border: 'none',
    width: '100%',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background var(--sc-transition-fast)',
  },
  sheetMenuItemText: {
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--sc-text-primary)',
  },
  sheetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
    marginTop: 18,
  },
  sheetGridItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px 8px',
    borderRadius: 'var(--sc-radius-lg)',
    cursor: 'pointer',
    transition: 'all var(--sc-transition-fast)',
  },
};
